// src/components/AcceptInvite.js
import { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { getInviteByToken, acceptInvite, getHousehold } from "../householdService";

export default function AcceptInvite({ token, onAccepted, onDeclined }) {
 const { user } = useAuth();
 const [invite, setInvite] = useState(null);
 const [status, setStatus] = useState("loading"); // loading | ready | accepting | error | already_member
 const [error, setError] = useState("");

 useEffect(() => {
 async function load() {
 try {
 const inv = await getInviteByToken(token);
 if (!inv) { setStatus("error"); setError("This invite link is invalid or has expired."); return; }
 if (inv.status !== "pending") { setStatus("error"); setError("This invite has already been used."); return; }

 // Check if user is already in this household
 if (user) {
 const household = await getHousehold(inv.householdId);
 if (household && household.memberIds.includes(user.uid)) {
 setStatus("already_member");
 setInvite(inv);
 return;
 }
 }

 setInvite(inv);
 setStatus("ready");
 } catch (err) {
 setStatus("error");
 setError("Failed to load invite. Please try again.");
 }
 }
 load();
 }, [token, user]);

 async function handleAccept() {
 if (!user) return;
 setStatus("accepting");
 try {
 await acceptInvite(invite, user);
 const household = await getHousehold(invite.householdId);
 onAccepted(household);
 } catch (err) {
 setStatus("error");
 setError("Failed to join household. Please try again.");
 }
 }

 return (
 <div style={styles.page}>
 <div style={styles.card}>
 <div style={{ fontSize: 52, textAlign: "center", marginBottom: 16 }}></div>

 {status === "loading" && (
 <div style={{ textAlign: "center", color: "#a07850", fontWeight: 700 }}>Loading invite...</div>
 )}

 {status === "error" && (
 <>
 <h2 style={{ ...styles.title, color: "#dc2626" }}>Invalid Invite</h2>
 <p style={styles.subtitle}>{error}</p>
 <button style={styles.btnSecondary} onClick={onDeclined}>Go to Dashboard</button>
 </>
 )}

 {status === "already_member" && (
 <>
 <h2 style={styles.title}>You're already a member!</h2>
 <p style={styles.subtitle}>You're already part of <strong>{invite?.householdName}</strong>.</p>
 <button style={styles.btn} onClick={onDeclined}>Go to Dashboard</button>
 </>
 )}

 {(status === "ready" || status === "accepting") && invite && (
 <>
 <h2 style={styles.title}>You've been invited!</h2>
 <p style={styles.subtitle}>
 <strong>{invite.inviterName}</strong> has invited you to join
 </p>
 <div style={styles.householdBadge}>
 {invite.householdName}
 </div>
 <p style={{ ...styles.subtitle, marginTop: 12 }}>
 You'll share access to all household repair tasks.
 </p>

 {!user && (
 <div style={styles.hint}>
 You need to be signed in to accept this invite. Please sign in or create an account first, then click the invite link again.
 </div>
 )}

 <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
 <button style={styles.btn} onClick={handleAccept} disabled={!user || status === "accepting"}>
 {status === "accepting" ? "Joining..." : " Accept & Join"}
 </button>
 <button style={styles.btnSecondary} onClick={onDeclined}>Decline</button>
 </div>
 </>
 )}
 </div>
 </div>
 );
}

const styles = {
 page: {
 minHeight: "100vh", background: "#fdf6ec",
 display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
 fontFamily: "'Nunito', sans-serif",
 },
 card: {
 background: "white", borderRadius: 24, padding: "40px 36px",
 width: "100%", maxWidth: 440,
 boxShadow: "0 8px 32px rgba(139,69,19,0.15)",
 },
 title: { fontFamily: "'Fredoka One', cursive", fontSize: 26, color: "#8B4513", marginBottom: 10, textAlign: "center" },
 subtitle: { fontSize: 14, color: "#a07850", fontWeight: 600, lineHeight: 1.6, textAlign: "center" },
 householdBadge: {
 background: "#fff7ed", border: "2px solid #fed7aa", borderRadius: 12,
 padding: "14px 20px", textAlign: "center", margin: "14px 0",
 fontFamily: "'Fredoka One', cursive", fontSize: 20, color: "#8B4513",
 },
 btn: {
 flex: 1, padding: "13px", border: "none",
 background: "linear-gradient(135deg, #ff8c00, #f97316)",
 borderRadius: 12, color: "white", fontFamily: "'Nunito', sans-serif",
 fontSize: 15, fontWeight: 800, cursor: "pointer",
 boxShadow: "0 4px 14px rgba(249,115,22,0.3)", transition: "all 0.18s",
 },
 btnSecondary: {
 flex: 1, padding: "12px", background: "white", border: "2px solid #e8d5b8",
 borderRadius: 12, color: "#7a5c3a", fontFamily: "'Nunito', sans-serif",
 fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "all 0.18s",
 },
 hint: {
 marginTop: 16, padding: "12px 16px", background: "#eff6ff",
 borderRadius: 12, border: "1px solid #bfdbfe",
 fontSize: 13, color: "#1e40af", fontWeight: 600, lineHeight: 1.6,
 },
};
